import { buildHighlightedSkillPath } from '../components/utils/nextSkillNavigation.js';
import {
  clearPendingGuestSkillClaim,
  loadPendingGuestSkillClaim,
} from './pendingGuestSkillClaim.js';
import { claimSkillReward } from './skillReward.js';

/**
 * @param {string|null|undefined} path
 */
export function isSafeRedirect(path) {
  return Boolean(path && path.startsWith('/') && !path.startsWith('//'));
}

/**
 * Resolve a post-auth return path from React Router state or a `?from=` query.
 * Static landing pages cannot pass location.state, so they use the query param.
 * @param {{ state?: { from?: string }, search?: string } | null | undefined} location
 * @param {string} [fallback]
 */
export function resolveAuthReturnPath(location, fallback = '/sectionHome') {
  const fromState = location?.state?.from;
  if (isSafeRedirect(fromState)) {
    return fromState;
  }

  const fromQuery = new URLSearchParams(location?.search || '').get('from');
  if (isSafeRedirect(fromQuery)) {
    return fromQuery;
  }

  return fallback;
}

/**
 * If the guest left a pending skill reward claim, record it and go to progressFeedback.
 * @returns {Promise<boolean>} true when navigation was handled
 */
export async function tryNavigatePendingGuestSkillClaim(navigate, userId) {
  const pending = loadPendingGuestSkillClaim();
  if (!pending) {
    return false;
  }

  try {
    const progressState = await claimSkillReward({
      userId,
      completionState: pending.completionState,
      themeMeta: {
        bigTopTheme: pending.bigTopTheme,
        theme: pending.theme,
      },
      recordCompletion: true,
    });

    clearPendingGuestSkillClaim();
    navigate('/progressFeedback', { replace: true, state: progressState });
    return true;
  } catch (error) {
    console.error('Failed to claim guest skill reward after auth:', error);
    return false;
  }
}

/**
 * Post-login navigation shared by Google and email/password auth.
 */

export async function navigateAfterAuth({
  navigate,
  from = '/sectionHome',
  userId,
  getLastSkill,
  findNextLearningOpportunity,
  needsTeacherOnboarding = false,
}) {
  if (needsTeacherOnboarding) {
    sessionStorage.setItem('accountIntent', 'teacher');
    navigate('/onboarding/teacher', { replace: true });
    return;
  }

  sessionStorage.removeItem('accountIntent');

  const handledPendingClaim = await tryNavigatePendingGuestSkillClaim(navigate, userId);
  if (handledPendingClaim) {
    return;
  }

  if (from && from !== '/sectionHome' && isSafeRedirect(from)) {
    navigate(from);
    return;
  }

  try {
    const lastSkill = await getLastSkill(userId);
    if (lastSkill?.lastSkillUnit && lastSkill?.lastSkillTopic) {
      if (lastSkill.lastAction === 'completed') {
        const nextSkill = await findNextLearningOpportunity(lastSkill.lastSkillId, userId);
        if (nextSkill) {
          const nextSkillPath = buildHighlightedSkillPath(nextSkill);
          if (nextSkillPath) {
            navigate(nextSkillPath);
            return;
          }
        }
      }

      navigate(`/skillsHome/${lastSkill.lastSkillUnit}/${lastSkill.lastSkillTopic}?highlight=${lastSkill.lastSkillId}`);
      return;
    }
  } catch (error) {
    console.error('Error resolving post-auth redirect:', error);
  }

  navigate('/sectionHome');
}
